import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../ui/alert-dialog';
import { 
  BarChart3, TrendingUp, PieChart, FileText, Loader2, Plus, Calendar,
  FlaskConical, RotateCcw, Trash2
} from 'lucide-react';
import { LineChart, Line, BarChart, Bar, PieChart as RechartsPieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const COLORS = ['#C87D3E', '#E9A94F', '#5C4023', '#8A7B6D', '#B84A3B', '#7A6248', '#D9B98F', '#3B2D21'];
const CHART_TOOLTIP_STYLE = { borderRadius: '12px', border: '1px solid #E0D6C8' };
const GRID_STROKE = "#E0D6C8";
const TICK_STYLE = { fontSize: 12 };

const AnalyticsTab = ({
  testMode,
  onToggleTestMode,
  analyticsData,
  analyticsLoading,
  timeRange,
  onTimeRangeChange,
  customReports,
  newReportName,
  setNewReportName,
  onCreateReport,
  onDeleteReport,
  resetting,
  onResetAnalytics
}) => {
  return (
    <div className="space-y-6">
      {/* Test Mode Card */}
      <Card className={`rounded-2xl border-2 ${testMode ? 'border-amber-400 bg-amber-50' : 'border-[#E0D6C8]'}`}>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${testMode ? 'bg-amber-100' : 'bg-[#F3EFE9]'}`}>
                <FlaskConical className={`w-6 h-6 ${testMode ? 'text-amber-600' : 'text-[#5A5A5A]'}`} />
              </div>
              <div>
                <h3 className="font-semibold text-[#1A1A1A]">Test Mode</h3>
                <p className="text-sm text-[#5A5A5A]">
                  {testMode 
                    ? 'Active - Resource Lists are NOT being tracked' 
                    : 'Inactive - Resource Lists are being tracked normally'}
                </p>
              </div>
            </div>
            <Button
              onClick={onToggleTestMode}
              data-testid="test-mode-toggle"
              className={`rounded-full px-6 ${
                testMode 
                  ? 'bg-amber-500 hover:bg-amber-600 text-white' 
                  : 'bg-[#5C4023] hover:bg-[#4A331C] text-white'
              }`}
            >
              {testMode ? 'Turn OFF' : 'Turn ON'}
            </Button>
          </div>
          {testMode && (
            <p className="mt-4 text-sm text-amber-700 bg-amber-100 rounded-lg p-3">
              Staff will see an amber banner indicating Test Mode is active. Use this when training new staff or testing the system.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Time Range Selector */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold font-['Manrope'] flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-[#5C4023]" />
          Resource Analytics
        </h2>
        <div className="flex items-center gap-2">
          <Select value={timeRange} onValueChange={onTimeRangeChange}>
            <SelectTrigger data-testid="time-range-select" className="w-40 rounded-xl">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="day">Last 24 Hours</SelectItem>
              <SelectItem value="week">Last Week</SelectItem>
              <SelectItem value="month">Last Month</SelectItem>
              <SelectItem value="quarter">Last Quarter</SelectItem>
              <SelectItem value="year">Last Year</SelectItem>
            </SelectContent>
          </Select>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                data-testid="reset-analytics-btn"
                className="rounded-xl border-[#E07A5F] text-[#E07A5F] hover:bg-[#E07A5F] hover:text-white"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="rounded-2xl">
              <AlertDialogHeader>
                <AlertDialogTitle>Reset Analytics Data?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete ALL analytics data including Resource List tracking history, 
                  resource usage stats, and category breakdowns. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
                <AlertDialogAction 
                  onClick={onResetAnalytics}
                  disabled={resetting}
                  className="bg-[#E07A5F] hover:bg-[#D06045] rounded-full"
                >
                  {resetting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Reset All Data
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {analyticsLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-[#5C4023]" />
        </div>
      ) : analyticsData ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="rounded-2xl border-[#E0D6C8]">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#5C4023]/10 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-[#5C4023]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#5A5A5A]">Total Resource Lists</p>
                    <p className="text-3xl font-bold text-[#1A1A1A]">{analyticsData.total_handouts}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="rounded-2xl border-[#E0D6C8]">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#E07A5F]/10 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-[#E07A5F]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#5A5A5A]">Resources Shared</p>
                    <p className="text-3xl font-bold text-[#1A1A1A]">{analyticsData.total_resources_shared}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="rounded-2xl border-[#E0D6C8]">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#3D405B]/10 flex items-center justify-center">
                    <PieChart className="w-6 h-6 text-[#3D405B]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#5A5A5A]">Categories Used</p>
                    <p className="text-3xl font-bold text-[#1A1A1A]">{analyticsData.category_breakdown.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Handouts Over Time Chart */}
          <Card className="rounded-2xl border-[#E0D6C8]">
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope']">Resource Lists Over Time</CardTitle>
              <CardDescription>Number of Resource Lists created per day</CardDescription>
            </CardHeader>
            <CardContent>
              {analyticsData.trend_data.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={analyticsData.trend_data}>
                    <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
                    <XAxis dataKey="date" tick={TICK_STYLE} />
                    <YAxis tick={TICK_STYLE} />
                    <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                    <Line 
                      type="monotone" 
                      dataKey="count" 
                      stroke="#5C4023" 
                      strokeWidth={3}
                      dot={{ fill: '#5C4023', strokeWidth: 2 }}
                      name="Resource Lists"
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-[#888888]">
                  No Resource List data yet. Create Resource Lists to see trends.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Two Column Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Resources */}
            <Card className="rounded-2xl border-[#E0D6C8]">
              <CardHeader>
                <CardTitle className="text-lg font-['Manrope']">Top Resources</CardTitle>
                <CardDescription>Most frequently shared resources</CardDescription>
              </CardHeader>
              <CardContent>
                {analyticsData.top_resources.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={analyticsData.top_resources.slice(0, 5)} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
                      <XAxis type="number" tick={TICK_STYLE} />
                      <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11 }} />
                      <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                      <Bar dataKey="count" fill="#5C4023" radius={[0, 8, 8, 0]} name="Times Shared" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-[#888888]">
                    No resource data yet.
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Category Breakdown */}
            <Card className="rounded-2xl border-[#E0D6C8]">
              <CardHeader>
                <CardTitle className="text-lg font-['Manrope']">Category Breakdown</CardTitle>
                <CardDescription>Most common need areas</CardDescription>
              </CardHeader>
              <CardContent>
                {analyticsData.category_breakdown.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <RechartsPieChart>
                      <Pie
                        data={analyticsData.category_breakdown}
                        dataKey="count"
                        nameKey="category"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label={({ category, percent }) => `${category.split(' ')[0]} ${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                      >
                        {analyticsData.category_breakdown.map((entry, idx) => (
                          <Cell key={`cell-${entry.category}`} fill={COLORS[idx % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                      <Legend />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-[#888888]">
                    No category data yet.
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Custom Reports Section */}
          <Card className="rounded-2xl border-[#E0D6C8]">
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope']">Saved Reports</CardTitle>
              <CardDescription>Create and manage custom reports</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  placeholder="Enter report name..."
                  value={newReportName}
                  onChange={(e) => setNewReportName(e.target.value)}
                  className="rounded-xl"
                  data-testid="new-report-name"
                />
                <Button 
                  onClick={onCreateReport}
                  className="bg-[#5C4023] hover:bg-[#4A331C] rounded-xl"
                  data-testid="create-report-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create
                </Button>
              </div>
              
              {customReports.length > 0 ? (
                <div className="space-y-2">
                  {customReports.map((report) => (
                    <div 
                      key={report.id}
                      className="flex items-center justify-between p-3 bg-[#F3EFE9]/50 rounded-xl"
                    >
                      <div>
                        <p className="font-medium text-[#1A1A1A]">{report.name}</p>
                        <p className="text-xs text-[#888888]">
                          Created {new Date(report.created_at).toLocaleDateString()} - {report.time_range}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onDeleteReport(report.id)}
                        className="h-8 w-8 text-[#E07A5F] hover:text-white hover:bg-[#E07A5F]"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-[#888888] text-center py-4">
                  No saved reports yet. Create one above.
                </p>
              )}
            </CardContent>
          </Card>
        </>
      ) : (
        <div className="text-center py-20 text-[#888888]">
          Click the Analytics tab to load data
        </div>
      )}
    </div>
  );
};

export default AnalyticsTab;
